import './UserInfo.sass';

import React, { FC } from 'react';

import { Header, ScrollView } from '$uikit';
import { format, formatCurrency } from '$utils';
import { CurrencyEnum } from '$store/models';
import { useEffect, useState } from 'react';
import { useRoute } from 'react-router5';
import { SummaryList } from '$store/lists/interface';
import { useTranslator } from '$hooks';
import { Api } from '$api';
import { UsersListItemProps } from '$shared/components/UsersListItem/UsersListItem.interface';
import { useDispatch } from 'react-redux';
import { toastActions } from '$store/toast';
import { Loader, UsersListItem } from '$shared/components';
import { UserInfoProps } from './UserInfo.interface';

export const UserInfo: FC<UserInfoProps> = (props) => {
  const t = useTranslator();
  const [data, setData] = useState<UsersListItemProps['data']>();
  const [isLoading, setIsLoading] = useState(true);
  const { route } = useRoute();
  const userId = props.userId || +route.params?.peerId;

  const dispatch = useDispatch();

  const getSum = (amount?: number) => {
    return formatCurrency(amount || 0, CurrencyEnum.EUR);
  };

  const formatDate = (timeStamp: number) => {
    return timeStamp ? format(new Date(timeStamp * 1000), 'dd MMM yyyy') : '-';
  };

  const getMappedData = (data: SummaryList) => {
    const { summary, followed, users } = data;

    const mappedData = summary.map((item) => {
      const { userId, total, purchasedTotal, tipsTotal, subscription } = item;
      const user = users[userId];
      let subscriptionExpireStatus = '-';

      if (subscription.createdAt && subscription.expiredAt === 0) {
        subscriptionExpireStatus = t('lists_list_collapse_subscription_expired');
      }

      if (subscription.expiredAt > 0) {
        subscriptionExpireStatus = formatDate(subscription.expiredAt);
      }

      return {
        userData: {
          ...user,
          isFollowFromMe: followed[userId],
        },
        collapseData: {
          title: t('lists_list_collapse_total_profit'),
          subTitle: getSum(total),
          tabs: [
            {
              tabName: t('lists_list_tab_profit'),
              tabContent: [
                {
                  title: t('lists_list_collapse_last_active'),
                  subTitle: formatDate(user.lastUpdate),
                },
                {
                  title: t('lists_list_collapse_sponsorship'),
                  subTitle: getSum(subscription.price),
                },
                {
                  title: t('lists_list_collapse_messages'),
                  subTitle: getSum(purchasedTotal),
                },
                {
                  title: t('lists_list_collapse_tips'),
                  subTitle: getSum(tipsTotal),
                },
                {
                  title: t('lists_list_collapse_total_profit'),
                  subTitle: getSum(total),
                  isHighlightedTitle: true,
                  isHighlightedLabel: true,
                },
              ],
            },
            {
              tabName: t('lists_list_tab_subscription'),
              tabContent: [
                {
                  title: t('lists_list_collapse_price'),
                  subTitle: getSum(subscription.price),
                  isHighlightedLabel: true,
                },
                {
                  title: t('lists_list_collapse_duration'),
                  subTitle: subscription.duration
                    ? t(`months_count`, { count: subscription.duration })
                    : '-',
                  isHighlightedLabel: true,
                },
                {
                  title: t('lists_list_collapse_started'),
                  subTitle: formatDate(subscription.createdAt),
                  isHighlightedLabel: true,
                },
                {
                  title: t('lists_list_collapse_expired'),
                  subTitle: subscriptionExpireStatus,
                  isHighlightedLabel: true,
                },
              ],
            },
          ],
        },
      };
    });

    return mappedData[0];
  };

  useEffect(() => {
    Api.get(`/lists/summary/${userId}`)
      // @ts-ignore
      .then((data: SummaryList) => {
        setData(getMappedData(data));
        setIsLoading(false);
      })
      .catch((e) => {
        dispatch(toastActions.setToastFail(e.message));
      });
  }, [userId]);

  return (
    <>
      <Header
        showBackButton={!props.onClose}
        showCloseButton={!!props.onClose}
        backButtonOnClick={props.onClose}
      >
        {t('user_info_title')}
      </Header>
      <ScrollView className="UserInfo">
        {!isLoading && !!data ? (
          <UsersListItem userId={userId} data={data} listId="" />
        ) : (
          <Loader isWrapped />
        )}
      </ScrollView>
    </>
  );
};
